'use client';

import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';
import { CareersSection } from '@/types/sanity';
import { Button } from '@/components/ui/button';
import { CTAButton } from '@/components/cta/CTAButton';
import Link from 'next/link';
import { ArrowRight, MapPin, Clock, Briefcase } from 'lucide-react';
import {
  ScrollReveal,
  StaggerContainer,
  staggerItemVariants,
} from '@/components/animations/ScrollAnimations';
import { cn } from '@/lib/utils';
import { formatTimeAgo } from '@/lib/utils/date';

const employmentTypeLabels: Record<string, string> = {
  'full-time': 'Full-time',
  'part-time': 'Part-time',
  contract: 'Contract',
  internship: 'Internship',
};

interface CareersSectionComponentProps {
  section: CareersSection;
  pageSlug?: string;
}

export function CareersSectionComponent({ section, pageSlug }: CareersSectionComponentProps) {
  if (section.showAllJobs) {
    return <FullJobListing section={section} pageSlug={pageSlug} />;
  }
  return <CuratedJobListing section={section} pageSlug={pageSlug} />;
}

// ─── Full Listing Mode (Careers Page) ────────────────────────────────────────

function FullJobListing({ section, pageSlug }: CareersSectionComponentProps) {
  const jobs = section.jobs || [];

  const employmentTypeMap: Record<string, string> = {
    'full-time': 'FULL_TIME',
    'part-time': 'PART_TIME',
    contract: 'CONTRACTOR',
    internship: 'INTERN',
  };

  const jobPostingsJsonLd = jobs.map((job) => ({
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    title: job.title,
    datePosted: job.postedAt,
    ...(job.description && {
      description: job.description
        .filter((b) => b._type === 'block')
        .map((b) => (b.children as Array<{ text: string }>)?.map((c) => c.text).join('') || '')
        .join(' '),
    }),
    ...(job.employmentType && { employmentType: employmentTypeMap[job.employmentType] }),
    ...(job.location && { jobLocation: { '@type': 'Place', address: job.location } }),
    ...(job.slug?.current && { url: `https://h3d.ai/careers/${job.slug.current}` }),
    hiringOrganization: {
      '@type': 'Organization',
      name: 'H3D',
      sameAs: 'https://h3d.ai',
    },
  }));

  return (
    <section
      className={cn(
        'careers-section py-8 md:py-12 lg:py-16',
        pageSlug && `careers-section--${pageSlug}`
      )}
    >
      {jobPostingsJsonLd.length > 0 && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jobPostingsJsonLd) }}
        />
      )}

      <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
        <div className="px-6 sm:px-0">
          <div className="mb-8 md:mb-10 text-center">
            {section.title && (
              <h2 className="font-medium mb-3 text-section-title leading-section-title">
                {section.title}
              </h2>
            )}
            {section.subtitle && (
              <p className="text-muted-foreground text-section-subtitle leading-section-subtitle">
                {section.subtitle}
              </p>
            )}
            {section.description && (
              <div className="prose prose-lg max-w-4xl mx-auto mt-4 text-left text-muted-foreground">
                <PortableText value={section.description} />
              </div>
            )}
          </div>

          <div className="max-w-4xl mx-auto">
            {jobs.length > 0 ? (
              <div className="rounded-xl border border-border/60 overflow-hidden">
                {jobs.map((job, index) => (
                  <Link
                    key={job._id}
                    href={`/careers/${job.slug?.current || ''}`}
                    className={cn(
                      'group block px-5 py-4 transition-colors hover:bg-primary/5',
                      index % 2 === 1 && 'bg-muted/40'
                    )}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-lg text-primary">{job.title}</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-muted-foreground">
                          {job.department && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-3.5 w-3.5" />
                              {job.department}
                            </span>
                          )}
                          {job.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {job.location}
                            </span>
                          )}
                          {job.employmentType && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {employmentTypeLabels[job.employmentType] || job.employmentType}
                            </span>
                          )}
                          {job.postedAt && (
                            <span className="text-xs">{formatTimeAgo(job.postedAt)}</span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:text-primary transition-all shrink-0" />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-muted-foreground text-lg">
                  No open positions available at the moment.
                </p>
              </div>
            )}
          </div>

          {section.ctas && section.ctas.length > 0 && (
            <div className="flex gap-4 justify-center mt-8 flex-wrap">
              {section.ctas.map((cta) => (
                <CTAButton key={cta._id} cta={cta} size="default" />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── Curated Mode (About Page, etc.) ─────────────────────────────────────────

function CuratedJobListing({ section, pageSlug }: CareersSectionComponentProps) {
  return (
    <ScrollReveal direction="up" delay={0.1}>
      <section
        className={cn(
          'careers-section py-8 md:py-12 lg:py-16',
          pageSlug && `careers-section--${pageSlug}`
        )}
      >
        <div className="mx-auto max-w-[90rem] sm:px-6 lg:px-8">
          <div className="px-6 sm:px-0">
            <div className="mb-8 md:mb-10 text-center">
              {section.title && (
                <h2 className="font-medium mb-3 text-section-title leading-section-title">
                  {section.title}
                </h2>
              )}
              {section.subtitle && (
                <p className="text-muted-foreground text-section-subtitle leading-section-subtitle">
                  {section.subtitle}
                </p>
              )}
              {section.description && (
                <div className="prose prose-lg max-w-4xl mx-auto mt-4 text-left text-muted-foreground">
                  <PortableText value={section.description} />
                </div>
              )}
            </div>

            <StaggerContainer
              staggerDelay={0.1}
              className="max-w-4xl mx-auto rounded-xl border border-border/60 overflow-hidden"
            >
              {section.jobs?.map((job, index) => (
                <motion.div key={job._id} variants={staggerItemVariants}>
                  <Link
                    href={`/careers/${job.slug?.current || ''}`}
                    className={cn(
                      'group block px-5 py-4 transition-colors hover:bg-primary/5',
                      index % 2 === 1 && 'bg-muted/40'
                    )}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-lg text-primary">{job.title}</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-muted-foreground">
                          {job.department && (
                            <span className="flex items-center gap-1">
                              <Briefcase className="h-3.5 w-3.5" />
                              {job.department}
                            </span>
                          )}
                          {job.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {job.location}
                            </span>
                          )}
                          {job.employmentType && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3.5 w-3.5" />
                              {employmentTypeLabels[job.employmentType] || job.employmentType}
                            </span>
                          )}
                          {job.postedAt && (
                            <span className="text-xs">{formatTimeAgo(job.postedAt)}</span>
                          )}
                        </div>
                      </div>
                      <ArrowRight className="h-5 w-5 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:text-primary transition-all shrink-0" />
                    </div>
                  </Link>
                </motion.div>
              ))}
            </StaggerContainer>

            <div className="flex gap-4 justify-center mt-8 flex-wrap">
              <Button variant="outline" asChild>
                <Link href="/careers" className="gap-2">
                  View all positions
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              {section.ctas?.map((cta) => (
                <CTAButton key={cta._id} cta={cta} size="default" />
              ))}
            </div>
          </div>
        </div>
      </section>
    </ScrollReveal>
  );
}
